import React from "react";
import axios from "axios";

import { triggerSnowplowImpressionEvent, triggerSnowplowVideoEvent } from "@themuse/snowplow-js-client";

import { pushDataLayerEvent } from "utils/tracking";
import { MetadataObj } from "./objects";
import { formatLog } from "./logging";

declare const jwplayer;

export const JWPLAYER_API_URL = "//cdn.jwplayer.com/v2/media/";

export interface JWPlaylistSource {
    width: number;
    height: number;
}

export interface VideoItem {
    duration: number;
    mediaid: string;
    sources: JWPlaylistSource[];
    tags: string; // Comma separated list of tags (from JWPlayer)
}

export interface VideoInfo {
    title: string;
    description: string;
    playlist: VideoItem[];
}

export interface InitJWPlayerVideoProps {
    container: React.RefObject<Element>;
    mute?: boolean;
    autostart?: boolean;
    videoInfo?: VideoInfo;
    generateSEOMetadata?: boolean;
}

export interface InitJwPlayerTrackingProps {
    playerInstance: MetadataObj,
    videoInfo: MetadataObj,
    position: string,
    element: HTMLElement,
}

const TRACKED_PLAYBACK_MILESTONES = [25, 50, 75, 100];

export const getJWPlayerVideoInfo = async (mediaID: string) => {
    try {
        const response = await axios.get(`${JWPLAYER_API_URL}${mediaID}`);
        return response.data;
    } catch (error) {
        console.error("Unable to fetch playlist info from JWPlayer API.", JSON.stringify(formatLog({ error })));
    }
    return null;
};

export const initJWPlayerVideo = ({
    container,
    videoInfo,
    autostart,
    mute,
    generateSEOMetadata
// eslint-disable-next-line no-undef
}: InitJWPlayerVideoProps): jwplayer.JWPlayer => {
    const { playlist } = videoInfo;
    if (typeof jwplayer !== "undefined") {
        return jwplayer(container.current).setup({
            playlist,
            autostart,
            mute,
            generateSEOMetadata
        });
    }
    return null;
};

export const initJWPlayerTracking = (props: InitJwPlayerTrackingProps) => {
    const {
        playerInstance,
        videoInfo,
        position,
        element
    } = props;
    Object.assign(window, { playerInstance });
    const config = playerInstance.getConfig();
    // only the first play is played, everything else is resumed.
    let started = false;
    let firstView = false;
    const getEventProps = (): MetadataObj => {
        const result: MetadataObj = {
            jw_video_source: position || null,
            jw_video_id: null,
            jw_video_name: videoInfo.title || null,
            jw_video_provider_key: config.id.replace("jwplayer-", "") || null,
            jw_video_provider_name: "jwplayer",
            jw_video_description: videoInfo.description || null,
            jw_video_autoplay: config.autostart || null,
            jw_video_muted: !!playerInstance.getMute(),
            jw_video_position: jwplayer().getPosition(),
        };
        return result;
    };

    playerInstance.on("viewable", () => {
        // The viewable event is bugged, so we add a getViewable check
        if (!firstView && playerInstance.getViewable()) {
            triggerSnowplowImpressionEvent(element);
            firstView = true;
        }
    });

    playerInstance.on("play", () => {
        const eventName = started ? "jwPlayerResumed" : "jwPlayerPlayed";
        pushDataLayerEvent(eventName, getEventProps());
        started = true;
        triggerSnowplowVideoEvent("play", element);
    });

    playerInstance.on("pause", () => pushDataLayerEvent("jwPlayedPaused", getEventProps()));

    playerInstance.on("mute", (event: MetadataObj) => {
        if (event.mute) {
            pushDataLayerEvent("jwPlayerMuted", getEventProps());
        } else {
            pushDataLayerEvent("jwPlayerUnmuted", getEventProps());
        }
    });

    playerInstance.on("fullscreen", () => {
        const fullscreen = playerInstance.getFullscreen();
        const dataLayerProps = {
            ...getEventProps(),
            jw_video_fullscreen: fullscreen,
            interaction_type: fullscreen ? "enter fullscreen" : "exit fullscreen"
        };
        pushDataLayerEvent("jwPlayerInteraction", dataLayerProps);
    });

    playerInstance.on("autostartNotAllowed", () => pushDataLayerEvent("jwPlayerAutostartNotAllowed", getEventProps()));

    playerInstance.on("seek", (event: MetadataObj) => {
        const dataLayerProps = {
            jw_video_seek_begin: event.position,
            jw_video_seek_end: event.offset,
            ...getEventProps(),
            interaction_type: event.position > event.offset ? "rewind" : "forward"
        };
        pushDataLayerEvent("jwPlayerInteraction", dataLayerProps);
    });

    playerInstance.on("time", (event: MetadataObj) => {
        const instance: MetadataObj & { latestPercentComplete?: number } = playerInstance;
        const videoPercentComplete = (event.position / event.duration) * 100;
        TRACKED_PLAYBACK_MILESTONES.forEach((milestone) => {
            const latestSeen = instance.latestPercentComplete || 0;
            if (videoPercentComplete >= milestone && milestone >= latestSeen) {
                pushDataLayerEvent("jwPlayerPlaybackMilestoneReached", {
                    video_percent_complete: milestone,
                    ...getEventProps()
                });
            }
        });
        instance.latestPercentComplete = videoPercentComplete;
    });
};
